import { SyncValidatorFn, ValidationResult } from './types';

/**
 *
 * @param validators a set of functions that needs to be validated in sequence
 * @returns the result of the first validator that returns a valid result.
 *
 * This function follows the "chain of responsibility" pattern.
 * The idea is, We start with the assumption that the user is already at the last step.
 * and then work our way backwards until we find a validator that is valid.
 *
 * (e.g) we assume the user is in the legal page first, if its not valid, we try
 * the previous step (i.e) billing page, then proof docs page and so on.
 *
 * This means, that the order of the validators is important. they are evaluated one by one
 * in the order they are passed in. So, make sure it matches the same order in which you
 * want the checks to be made.
 */
export function startSynchronousValidationChain(
  ...validators: SyncValidatorFn[]
): ValidationResult | null {
  let result: ValidationResult | null = null;
  /**
   * The validators are intentionally typed as SyncValidatorFn.
   * This is to ensure that the function is not async.
   * While we cannot enforce them to not return a promise at runtime, this typing
   * shows intention that the validationChain prefers them to be synchronous.
   * Since this is a chain of functions executed in sequence, if they are asynchronous,
   * one validator can block the entire chain. Any asynchronous data needed for the validation
   * should be fetched before calling startValidationChain.
   * (check getEssentials() in SponsoredSolarRouterController for example)
   */
  for (const validator of validators) {
    if (!result?.isValid) {
      result = validator();
    } else {
      break;
    }
  }

  return result;
}
